/**
 * Wizard state — held in React context across the 3 steps of
 * /admin/properties/new: Property → Apartments → Review.
 *
 * COL-252: owner + revenue are configured PER APARTMENT (not globally). There
 * is no standalone Lessor or Revenue step anymore — each apartment carries its
 * own owner, revenue model, fees and rooms. Nothing persists until Review submit.
 */
import { createContext, useContext } from "react";
import type {
  CollectionMode,
  PropertyType,
  RevenueModel,
} from "@/types/property";
import { type ChecklistState, newChecklist } from "./checklist";

export type WizardRoom = {
  /** Local-only id for keying — replaced with DB id post-submit. */
  localId: string;
  title: string;
  price: string; // string for form binding; coerced to number on submit
  size_sqm: string;
  floor: string;
  keynest_pickup_code: string;
  /** Default-language (English) description; mirrored to legacy
   *  `description` column on submit. The first room's value carries over to
   *  sibling rooms that are still blank (see ApartmentCard in
   *  Step2Apartments). */
  descriptionEn: string;
  /** German translation. Empty = not set, omitted from JSONB on submit. */
  descriptionDe: string;
  /** Public URLs of already-uploaded room photos (first = primary cover). */
  images: string[];
};

/**
 * An apartment (owner-owned unit) configured in the wizard. Owner, revenue
 * model and rooms are all set here (COL-252). The owner is the lessor by
 * default — `ownerUserId` points at the property owner; `lessorId` is an
 * optional override for the exception case. Rooms are nested and created
 * linked to the apartment.
 */
export type WizardApartment = {
  /** Local-only id for keying — replaced with DB id post-submit. */
  localId: string;
  label: string;
  floor: string;
  size_sqm: string;
  /** Declared number of rooms — drives fee-per-room totals (COL-252). */
  numRooms: string;
  // Owner (owner = lessor by default; lessorId is an override)
  ownerUserId: string | null;
  lessorId: string | null;
  /** Representor override for this apartment ("vertreten durch" on contracts).
   *  Blank = inherit the owner's default. */
  repName: string;
  repNote: string;
  // Revenue / collection (per apartment). NULL = inherit from the property
  // — matches apartments.collection_mode/revenue_model (COL-225): an
  // apartment that never sets its own value keeps following the property's,
  // including later property-level edits, the same way ApartmentEditSheet's
  // "Inherit from property" option behaves for an apartment added to an
  // existing property.
  collectionMode: CollectionMode | null;
  revenueModel: RevenueModel | null;
  /** Used when revenueModel === 'pct_of_rent'. */
  coliviqFeePct: string;
  /** Used when revenueModel === 'flat_fee_per_room'. */
  coliviqFeeAmount: string;
  /** Master rent CoLiviq pays the owner (full_rent model). */
  masterRent: string;
  rooms: WizardRoom[];
  /** The 15 go-live proofs tracked for this unit (COL-529, Step 3). */
  checklist: ChecklistState;
};

export type WizardState = {
  property: {
    title: string;
    /**
     * The `properties.address` column ("Street + house number"). Derived by
     * `setProperty` from street_name/street_number (see composeStreetAddress)
     * whenever either changes — there is no input that sets this directly.
     */
    address: string;
    /**
     * Street WITHOUT the house number, spelled out in full ("Baierbrunnerstrasse").
     * Its own column because contracts and the Wohnungsgeberbestätigung print
     * street and house number in separate boxes — see validateStreetName for
     * why abbreviations are rejected.
     */
    street_name: string;
    /** House number on its own ("1", "12a"). */
    street_number: string;
    city: string;
    /** German postal code (PLZ). Free-form text. */
    zip_code: string;
    /**
     * Coordinates from the address autocomplete's own geocoding response.
     * `null` until an address is picked from the suggestions — typing a street
     * by hand leaves the property ungeocoded rather than guessing a point.
     *
     * The public room map reads these (COL-48). Before they were captured the
     * wizard wrote 0/0 placeholders, which put every new property's pin in the
     * Atlantic off West Africa.
     */
    lat: number | null;
    lng: number | null;
    /**
     * COL-529: whether a KeyNest partner shop is within 15 km of this property
     * (resolved from lat/lng by useKeyNestAvailability at the wizard root).
     * Gates the "KeyNest partner" key-handover option in the Step 3 checklist.
     * Defaults false until proven — a missing/failed lookup falls back to
     * in-person handover rather than offering a dead option.
     */
    keynestAvailable: boolean;
    type: PropertyType | "";
    /** Default-language (English) description — mirrored to legacy `description`. */
    description: string;
    /** German translation. Empty string = not set. */
    descriptionDe: string;
    /** Number of apartments (owner-owned units) to set up in the Apartments step. */
    aptCount: string;
    /** Estimated rooms for the single apartment when aptCount === "1". */
    estimatedRoomCount: string;
    /** Public URLs of already-uploaded property photos (first = cover). */
    images: string[];
    /** Optional 360°/Matterport/etc tour URL. */
    virtualTourUrl: string;
    /** Revenue / collection — staff-only (COL-252 hides these from owner
     *  submissions). Seeds the default for every apartment created in this
     *  property; each apartment can still override it individually. */
    collectionMode: CollectionMode;
    revenueModel: RevenueModel;
    /** Used when revenueModel === 'pct_of_rent'. */
    coliviqFeePct: string;
    /** Used when revenueModel === 'flat_fee_per_room'. */
    coliviqFeeAmount: string;
    /** Master rent CoLiviq pays the owner (full_rent model). */
    masterRent: string;
  };
  /** Apartments, each holding its own owner, revenue config and rooms. */
  apartments: WizardApartment[];
  /**
   * Staff-only "request from owner" email composed on the Review step (COL-529
   * point 4). Lists the checklist items the admin flagged as request-from-owner.
   * The admin edits it and sends it directly from the Review step (from
   * info@coliviq.de), per Neel's feedback — there is no send-on-submit.
   * `prefilled` guards the one-time prefill so a later re-render never clobbers
   * the admin's edits; `sent` keeps the sent state across step navigation so the
   * same draft can't be sent twice. Not persisted — lives in wizard memory.
   */
  ownerEmail: {
    to: string;
    subject: string;
    body: string;
    /** True once the draft has been prefilled from the checklist. */
    prefilled: boolean;
    /** True once the admin has sent it from the Review step. */
    sent: boolean;
  };
  /**
   * Set only when editing a submission that predates the go-live checklist
   * (read-back found zero `apartment_checklist_items` rows — see
   * useSubmissionForEdit). Tells validateWizardStep to grandfather the owner
   * 15/15 + smoke gate for this edit, matching the server's legacy-skip in
   * owner_resubmit_property. Absent/false for every new submission. Survives
   * setProperty/setApartments because both spread the whole state (COL-529).
   */
  checklistGrandfathered?: boolean;
};

export const INITIAL_WIZARD_STATE: WizardState = {
  property: {
    title: "",
    address: "",
    street_name: "",
    street_number: "",
    city: "",
    zip_code: "",
    lat: null,
    lng: null,
    keynestAvailable: false,
    type: "",
    description: "",
    descriptionDe: "",
    aptCount: "1",
    estimatedRoomCount: "",
    images: [],
    virtualTourUrl: "",
    collectionMode: "company_collects",
    revenueModel: "flat_fee_per_room",
    coliviqFeePct: "",
    coliviqFeeAmount: "",
    masterRent: "",
  },
  apartments: [],
  ownerEmail: {
    to: "",
    subject: "",
    body: "",
    prefilled: false,
    sent: false,
  },
};

export type WizardContextValue = {
  state: WizardState;
  setProperty: (patch: Partial<WizardState["property"]>) => void;
  setApartments: (apartments: WizardApartment[]) => void;
  setOwnerEmail: (patch: Partial<WizardState["ownerEmail"]>) => void;
  reset: () => void;
};

export const WizardContext = createContext<WizardContextValue | null>(null);

export function useWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) {
    throw new Error("useWizard must be used inside <WizardProvider>");
  }
  return ctx;
}

export function newRoom(): WizardRoom {
  return {
    localId: cryptoRandomId(),
    title: "",
    price: "",
    size_sqm: "",
    floor: "",
    keynest_pickup_code: "",
    descriptionEn: "",
    descriptionDe: "",
    images: [],
  };
}

export function newApartment(label = ""): WizardApartment {
  return {
    localId: cryptoRandomId(),
    label,
    floor: "",
    size_sqm: "",
    numRooms: "",
    ownerUserId: null,
    lessorId: null,
    repName: "",
    repNote: "",
    // Inherit from the property by default (see the WizardApartment comment
    // above) — a new apartment doesn't propose terms, it follows whatever
    // the property has, including later edits, until explicitly overridden.
    collectionMode: null,
    revenueModel: null,
    coliviqFeePct: "",
    coliviqFeeAmount: "",
    masterRent: "",
    rooms: [],
    checklist: newChecklist(),
  };
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 11);
}
